import { API_BASE_URL } from '../config/api';
import axios from 'axios';

export interface PostComment {
  id: string;
  userId: string;
  userName: string;
  content: string;
  createdAt: string;
}

export interface Post {
  id: string;
  dietitianId: string;
  dietitianName: string;
  title: string;
  content: string;
  type: 'article' | 'news' | 'product';
  price?: number;
  productUrl?: string;
  imageUrl?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  isPublished: boolean;
  /** "public" = Herkese açık, "clients_only" = Sadece danışanlarıma */
  visibility?: 'public' | 'clients_only';
  likedBy?: string[];
  likeCount?: number;
  comments?: PostComment[];
}

export interface CreatePostDto {
  dietitianId: string;
  dietitianName: string;
  title: string;
  content: string;
  type: 'article' | 'news' | 'product';
  price?: number;
  productUrl?: string;
  imageUrl?: string;
  tags?: string[];
  isPublished?: boolean;
  visibility?: 'public' | 'clients_only';
}

export const postApi = {
  // Tüm postları getir
  getAllPosts: async (): Promise<Post[]> => {
    const response = await axios.get(`${API_BASE_URL}/posts`);
    return response.data;
  },

  // Diyetisyenin tüm postlarını getir (kendi postları için)
  getPostsByDietitianId: async (dietitianId: string): Promise<Post[]> => {
    const response = await axios.get(`${API_BASE_URL}/posts/dietitian/${dietitianId}`);
    return response.data;
  },

  // Diyetisyenin yayınlanmış postlarını getir (danışanlar için)
  getPublishedPostsByDietitianId: async (dietitianId: string): Promise<Post[]> => {
    const response = await axios.get(`${API_BASE_URL}/posts/dietitian/${dietitianId}/published`);
    return response.data;
  },

  // Herkese açık top paylaşımlar (login gerekmez)
  getTopPublicPosts: async (limit = 20): Promise<Post[]> => {
    const response = await axios.get(`${API_BASE_URL}/posts/public/top`, { params: { limit } });
    return response.data;
  },

  // ID'ye göre post getir
  getPostById: async (id: string): Promise<Post> => {
    const response = await axios.get(`${API_BASE_URL}/posts/${id}`);
    return response.data;
  },

  // Yeni post oluştur
  createPost: async (postData: CreatePostDto): Promise<Post> => {
    const response = await axios.post(`${API_BASE_URL}/posts`, postData);
    return response.data;
  },

  // Post güncelle
  updatePost: async (id: string, postData: Partial<CreatePostDto>): Promise<Post> => {
    const response = await axios.put(`${API_BASE_URL}/posts/${id}`, postData);
    return response.data;
  },

  // Post sil
  deletePost: async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/posts/${id}`);
  },

  // Yayınlama durumunu değiştir
  togglePublish: async (id: string): Promise<Post> => {
    const response = await axios.post(`${API_BASE_URL}/posts/${id}/toggle-publish`);
    return response.data;
  },

  // Tipe göre postları getir
  getPostsByType: async (dietitianId: string, type: string): Promise<Post[]> => {
    const response = await axios.get(`${API_BASE_URL}/posts/dietitian/${dietitianId}/type/${type}`);
    return response.data;
  },

  // Post beğen/beğenmeyi kaldır
  toggleLike: async (postId: string, userId: string): Promise<Post> => {
    const response = await axios.post(`${API_BASE_URL}/posts/${postId}/like`, { userId });
    return response.data;
  },

  // Yorum ekle
  addComment: async (postId: string, userId: string, userName: string, content: string): Promise<Post> => {
    const response = await axios.post(`${API_BASE_URL}/posts/${postId}/comment`, {
      userId,
      userName,
      content,
    });
    return response.data;
  },

  // Yorum sil
  deleteComment: async (postId: string, commentId: string): Promise<Post> => {
    const response = await axios.delete(`${API_BASE_URL}/posts/${postId}/comment/${commentId}`);
    return response.data;
  },

  // Yorum güncelle
  updateComment: async (postId: string, commentId: string, content: string): Promise<Post> => {
    const response = await axios.put(`${API_BASE_URL}/posts/${postId}/comment/${commentId}`, {
      content,
    });
    return response.data;
  },
};

export default postApi;

